import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { popularModels, type SparePart } from "@/mocks/products";
import { imageFor } from "@/lib/productImages";

interface Product {
  id: string;
  category: string;
  name: string;
  make: string | null;
  model: string | null;
  year_range: string | null;
  fitment: string | null;
  price: string | null;
  warranty: string | null;
  condition: string | null;
  stock: string | null;
  description: string | null;
  image_url: string | null;
}

const CATEGORIES = ["Engines", "Transmissions", "Body Parts"];

// Builds a SparePart-shaped object so the shared image resolver can pick the
// right image for a database product that has no photo stored yet.
function toSpare(p: Product): SparePart {
  return {
    id: p.id,
    name: p.name,
    category: p.category as SparePart["category"],
    make: p.make || "Universal",
    model: p.model || "",
    yearRange: p.year_range || "",
    fitment: p.fitment || "",
    price: p.price || "",
    warranty: p.warranty || "",
    condition: p.condition || "",
    stock: p.stock || "",
    description: p.description || "",
    image_url: p.image_url || undefined,
  };
}

const emptyForm = {
  category: "Engines",
  name: "",
  make: "",
  model: "",
  year_range: "",
  fitment: "",
  price: "",
  warranty: "3 Month Used Parts Warranty",
  condition: "Used",
  stock: "In Stock",
  description: "",
  image_url: "",
};

export default function ProductsManager() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<Product | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase
        .from("products")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (err) throw err;
      setProducts((data as Product[]) || []);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = products.filter((p) => {
    const matchesCategory = categoryFilter === "All" || p.category === categoryFilter;
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      [p.name, p.make, p.model, p.fitment].filter(Boolean).join(" ").toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      category: p.category,
      name: p.name,
      make: p.make || "",
      model: p.model || "",
      year_range: p.year_range || "",
      fitment: p.fitment || "",
      price: p.price || "",
      warranty: p.warranty || "",
      condition: p.condition || "",
      stock: p.stock || "",
      description: p.description || "",
      image_url: p.image_url || "",
    });
    setModalOpen(true);
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (JPG, PNG or WebP).");
      return;
    }
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const fileName = `product-${Date.now()}.${ext}`;
    setUploading(true);
    try {
      const { error: uploadError } = await supabase.storage
        .from("products")
        .upload(fileName, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from("products").getPublicUrl(fileName);
      setForm((f) => ({ ...f, image_url: data.publicUrl }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      category: form.category,
      name: form.name,
      make: form.make || null,
      model: form.model || null,
      year_range: form.year_range || null,
      fitment: form.fitment || null,
      price: form.price || null,
      warranty: form.warranty || null,
      condition: form.condition || null,
      stock: form.stock || null,
      description: form.description || null,
      image_url: form.image_url || null,
    };

    const { error: saveError } = editing
      ? await supabase.from("products").update(payload).eq("id", editing.id)
      : await supabase.from("products").insert(payload);

    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setModalOpen(false);
    load();
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const { error: delError } = await supabase
      .from("products")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", deleting.id);
    setDeleting(null);
    if (delError) {
      setError(delError.message);
      return;
    }
    load();
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === filtered.length) return new Set();
      return new Set(filtered.map((p) => p.id));
    });
  };

  const bulkDelete = async () => {
    const { error: delError } = await supabase
      .from("products")
      .update({ deleted_at: new Date().toISOString() })
      .in("id", Array.from(selected));
    setConfirmBulk(false);
    if (delError) {
      setError(delError.message);
      return;
    }
    setSelected(new Set());
    load();
  };

  const setField = (key: keyof typeof emptyForm, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Persists a resolved image URL for every product that does not have one
  // saved yet, so all images live in the database rather than being resolved
  // on the fly at render time.
  const syncImages = async () => {
    setSyncing(true);
    setError("");
    setNotice("");
    try {
      const { data, error: loadErr } = await supabase
        .from("products")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (loadErr) throw loadErr;
      const rows = (data as Product[]) || [];
      // Resolve every missing image URL up-front, then persist all of them in a
      // single batch call — this turns ~120 sequential requests into one, so the
      // button no longer sits on "Saving…" for a long time.
      const updates = rows
        .map((p, i) => {
          const url = imageFor(toSpare(p), i);
          return url && url !== p.image_url ? { id: p.id, image_url: url } : null;
        })
        .filter((u): u is { id: string; image_url: string } => u !== null);

      if (updates.length > 0) {
        const { error: updErr } = await supabase
          .from("products")
          .upsert(updates, { onConflict: "id" });
        if (updErr) throw updErr;
      }
      setNotice(
        updates.length > 0
          ? `${updates.length} product image${updates.length === 1 ? "" : "s"} saved to the database.`
          : "All product images are already saved."
      );
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save images.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">Products</h2>
          <p className="mt-1 text-sm text-neutral-500">{products.length} products in inventory.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            onClick={syncImages}
            disabled={syncing}
            title="Save every product image into the database"
            className="flex items-center justify-center gap-2 rounded-md border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-neutral-700 transition hover:bg-neutral-100 disabled:opacity-60 whitespace-nowrap"
          >
            {syncing ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-database-2-line"></i>
            )}
            {syncing ? "Saving…" : "Save Images to Database"}
          </button>
          <button
            onClick={openAdd}
            className="flex items-center justify-center gap-2 rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add Product
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, make or model…"
            className="w-full rounded-md border border-neutral-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-brand-gold focus:outline-none"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
        >
          <option value="All">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError("")} className="text-red-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {notice && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <span className="text-sm text-green-700">{notice}</span>
          <button onClick={() => setNotice("")} className="text-green-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="mt-4 flex items-center justify-between rounded-md bg-brand-dark px-4 py-3 text-white">
          <span className="text-sm">{selected.size} selected</span>
          <button
            onClick={() => setConfirmBulk(true)}
            className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            Bulk Delete
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="h-4 w-4 accent-brand-dark"
                  />
                </th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Price</th>
                <th className="px-4 py-3 font-medium">Stock</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="h-4 w-4 accent-brand-dark"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="h-10 w-12 shrink-0 rounded border border-neutral-200 bg-neutral-100 object-cover object-top"
                        />
                      ) : (
                        <span className="flex h-10 w-12 shrink-0 items-center justify-center rounded border border-neutral-200 bg-neutral-100 text-neutral-300">
                          <i className="ri-image-line"></i>
                        </span>
                      )}
                      <div>
                        <p className="font-medium text-brand-dark">{p.name}</p>
                        <p className="text-xs text-neutral-500">
                          {[p.make, p.model].filter(Boolean).join(" ")}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600">
                      {p.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-brand-dark">{p.price || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">{p.stock || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100"
                        title="Edit"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        onClick={() => setDeleting(p)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-red-500 hover:bg-red-50"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-neutral-400">
                    No products match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <div className="my-8 w-full max-w-2xl rounded-lg bg-white">
            <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
              <h3 className="font-heading text-lg font-semibold uppercase text-brand-dark">
                {editing ? "Edit Product" : "Add Product"}
              </h3>
              <button onClick={() => setModalOpen(false)} className="text-neutral-400 hover:text-brand-dark">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSave} className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
              <div className="sm:col-span-2 rounded-md bg-neutral-50 p-3">
                <label className="block text-sm font-medium text-brand-dark">
                  Quick-select make &amp; model
                </label>
                <select
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) return;
                    const [mk, mdl] = val.split("||");
                    setForm((f) => ({ ...f, make: mk, model: mdl }));
                  }}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                >
                  <option value="">Choose a popular vehicle…</option>
                  {popularModels.map((pm) => (
                    <option key={`${pm.make}-${pm.model}`} value={`${pm.make}||${pm.model}`}>
                      {pm.make} {pm.model}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-neutral-500">
                  Selecting a vehicle auto-fills the Make and Model fields below.
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-brand-dark">Product Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Price</label>
                <input
                  value={form.price}
                  onChange={(e) => setField("price", e.target.value)}
                  placeholder="$4,850"
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Make</label>
                <input
                  value={form.make}
                  onChange={(e) => setField("make", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Model</label>
                <input
                  value={form.model}
                  onChange={(e) => setField("model", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Year Range</label>
                <input
                  value={form.year_range}
                  onChange={(e) => setField("year_range", e.target.value)}
                  placeholder="2013 - 2018"
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Fitment</label>
                <input
                  value={form.fitment}
                  onChange={(e) => setField("fitment", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Condition</label>
                <input
                  value={form.condition}
                  onChange={(e) => setField("condition", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Stock</label>
                <input
                  value={form.stock}
                  onChange={(e) => setField("stock", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-dark">Warranty</label>
                <input
                  value={form.warranty}
                  onChange={(e) => setField("warranty", e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-brand-dark">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  rows={3}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                />
              </div>

              <div className="sm:col-span-2 rounded-md border border-neutral-200 bg-neutral-50 p-4">
                <label className="block text-sm font-medium text-brand-dark">Product Photo</label>
                <p className="mt-1 text-xs text-neutral-500">
                  Upload a real photo of this part, or paste an image URL. Leave blank to use a generated placeholder.
                </p>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
                  {form.image_url ? (
                    <img
                      src={form.image_url}
                      alt="Product preview"
                      className="h-24 w-32 rounded-md border border-neutral-200 bg-white object-cover object-top"
                    />
                  ) : (
                    <div className="flex h-24 w-32 items-center justify-center rounded-md border border-dashed border-neutral-300 bg-white text-neutral-400">
                      <i className="ri-image-line text-2xl"></i>
                    </div>
                  )}
                  <div className="flex flex-1 flex-col gap-2">
                    <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 whitespace-nowrap">
                      {uploading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Uploading…
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-2-line"></i>
                          Upload Photo
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file);
                        }}
                      />
                    </label>
                    <input
                      value={form.image_url}
                      onChange={(e) => setField("image_url", e.target.value)}
                      placeholder="…or paste an image URL"
                      className="w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                    />
                    {form.image_url && (
                      <button
                        type="button"
                        onClick={() => setField("image_url", "")}
                        className="self-start text-xs font-medium text-red-500 hover:text-red-600"
                      >
                        Remove photo
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 flex justify-end gap-3 border-t border-neutral-200 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 rounded-md bg-brand-gold px-6 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
                >
                  {saving && <i className="ri-loader-4-line animate-spin"></i>}
                  {saving ? "Saving…" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-brand-dark">Delete product?</h3>
            <p className="mt-2 text-sm text-neutral-500">
              “{deleting.name}” will be moved to Trash — you can restore it later.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-brand-dark">
              Delete {selected.size} products?
            </h3>
            <p className="mt-2 text-sm text-neutral-500">They'll be moved to Trash and can be restored.</p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setConfirmBulk(false)}
                className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={bulkDelete}
                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}